import React, { type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
// Actions.
import { postCustomerDetails } from "../../actions/customerData";
// Types.
import type {
	Customer,
	UpdateInfoForm,
	UpdateInfoFormProps,
} from "../../types/customer";
// Utils.
import { validateEmail, validatePhoneNumber } from "../../utils/contact";
import { gaEventIfAllowed } from "../../utils/googleAnalytics";
import Button from "../Common/Button";
import Loading from "../Common/Loading";
import styles from "./styles/UpdateMyInfoForm.module.scss";
import { hideUpdateInfo } from "./MyInfo";

const initialState = {
	number: "",
	email: "",
};

const UpdateMyInfo: React.FunctionComponent<UpdateInfoFormProps> = (
	props: UpdateInfoFormProps,
) => {
	const [formState, updateFormState] =
		React.useState<UpdateInfoForm>(initialState);
	const [updateUserData, setUpdateUserData] = React.useState<boolean>(false);
	const [dataUpdated, setDataUpdated] = React.useState<boolean>(false);
	const [updateError, setUpdateError] = React.useState<boolean>(false);
	const [validEmail, setValidEmail] = React.useState<boolean>(true);
	const [validNumber, setValidNumber] = React.useState<boolean>(true);
	const { customer, endpoint, setUpdateUsers } = props;
	const { t } = useTranslation();

	React.useEffect(() => {
		const customerNumber = customer.userNumber ?? "";
		const customerEmail = customer.userEmail ?? "";

		const formDetails = {
			number: customerNumber,
			email: customerEmail,
		};

		updateFormState(formDetails);
	}, [customer]);

	React.useEffect(() => {
		async function updateUserDetails(): Promise<void> {
			const detailsToUpdate = {
				number: formState.number,
				email: formState.email,
			};

			const customerId = customer.userId;

			const updatedUserData = await postCustomerDetails(
				customerId,
				customerId,
				detailsToUpdate,
			);

			if (updatedUserData as Customer) {
				updateFormState({
					number: (updatedUserData as Customer).mobileNumber ?? "",
					email: (updatedUserData as Customer).email,
				});
				setDataUpdated(true);
				setUpdateUserData(false);
				setUpdateUsers(true);
			} else {
				setUpdateUserData(true);
			}
		}
		if (updateUserData) {
			updateUserDetails();
		}
	}, [updateUserData, formState, customer]);

	const handleFormSubmit = (): void => {
		gaEventIfAllowed({
			category: "Omien tietojen päivittäminen",
			action: "Yhteystiedot tallennettu",
			label: `${t(`myInfo:customerInfo.submitForm${endpoint}`)}`,
		});

		setUpdateUserData(true);
	};

	const updateUserForm = (e: ChangeEvent<HTMLInputElement>): void => {
		const value: string = e.target.value;
		const elementName: string = e.target.name;
		const currentFormState = formState;

		switch (elementName) {
			case "mail":
				currentFormState.email = value;
				setValidEmail(validateEmail(value));
				break;
			case "phone":
				currentFormState.number = value;
				setValidNumber(validatePhoneNumber(value));
				break;
		}
		setDataUpdated(false);
		setUpdateError(false);
		updateFormState((prev: UpdateInfoForm) => ({ ...prev, currentFormState }));
	};

	const customerId = customer.userId;

	return (
		<form className={styles.form}>
			<div className={styles.formItems}>
				<div className={`form-item ${styles.formItem}`}>
					<label htmlFor={`user-${customerId}-phone-${endpoint}`}>
						{t("common:general.phone")}
					</label>
					<input
						type="text"
						name="phone"
						id={`user-${customerId}-phone-${endpoint}`}
						value={formState.number}
						onChange={(e): void => updateUserForm(e)}
            disabled={hideUpdateInfo}
					/>
					<span className="contact-error">
						{(!validNumber || !formState.number) &&
							t("common:contactform.phone")}
						&nbsp;
					</span>
				</div>
				<div className={`form-item ${styles.formItem}`}>
					<label htmlFor={`user-${customerId}-email-${endpoint}`}>
						{t("common:general.email")}
					</label>
					<input
						type="email"
						id={`user-${customerId}-email-${endpoint}`}
						name="mail"
						value={formState.email}
						onChange={(e): void => updateUserForm(e)}
            disabled={hideUpdateInfo}
					/>
					<span className="contact-error">
						{!validEmail && t("common:contactform.email")}&nbsp;
					</span>
				</div>
			</div>

			{updateUserData && <Loading size="small" />}
			{dataUpdated && (
				<p aria-live="polite">{t("common:messages.updateSuccess")}</p>
			)}
			{updateError && (
				<p aria-live="polite">{t("common:messages.updateError")}</p>
			)}
			<Button
				type="submit"
				aria-label={t(`myInfo:customerInfo.submitForm${endpoint}`)}
				disabled={!validEmail || !validNumber || hideUpdateInfo ? true : false}
				onClick={(e): void => {
					e.preventDefault();
					// @ts-expect-error
					if (e.target.ariaDisabled === "false" && !hideUpdateInfo) {
						handleFormSubmit();
					}
				}}
			>
				{t("common:general.save")}
			</Button>
		</form>
	);
};

export default UpdateMyInfo;
